import { User } from '../models/user';
import { MatDialog } from '@angular/material/dialog';
import { Injectable } from '@angular/core';
import { UserSelectionDialogComponent } from 'app/main/content/users/user-selection-dialog/user-selection-dialog.component';
import { MessageBoxComponent, MessageBoxParameters, MessageBoxType } from 'app/components/messageBox/messageBox.component';
import { UserService } from '../services/user.service';
import { Subject } from '../models/subject';
import { Observable, of, ReplaySubject } from 'rxjs';
import { UserSubjectHistoryService } from './user-subject-history.service';
import { EventService } from './event.service';
import { take } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { SubjectService } from './subject.service';
import { HyperLawCase, HyperLawService } from './hyper-law.service';
import { SubjectUserSecurityComponent } from 'app/main/content/subjects/subject-user-security/subject-user-security.component';
import { UserSecurityDto } from '../models/user-security-dto';

@Injectable({ providedIn: 'root' })
export class UserSecurityService {

  onSecurityChanged: ReplaySubject<any> = new ReplaySubject<any>(1, 100);

  constructor(
    public dialog: MatDialog,
    private userService: UserService,
    private subjectService: SubjectService,
    private eventService: EventService,
    private userSubjectHistoryService: UserSubjectHistoryService,
    private hyperLawService: HyperLawService,
  ) {}

  apply(entity: Subject, selectedUsers: number[]) {
    let getUsers$: Observable<User[]> = of([]);

    if (environment.limitAccessToSubjectsByOrg === true) {
      if (entity && entity.subjectOrganisations && entity.subjectOrganisations.length > 0) {
        const subjectOrgId = entity.subjectOrganisations[0].organisationId;
        getUsers$ = this.userService.getActiveUsersForOrganisation(subjectOrgId, this.userService.getCurrentUser().isAdmin);
      }
    }
    
    getUsers$.pipe(take(1)).subscribe((SubjectAllowedUsersList) => {
      this.showDlg(entity, selectedUsers, SubjectAllowedUsersList);
    });
  }

  showDlg(entity: Subject, selectedUsers: number[], userList: User[] = []) {
    const initialUsers = selectedUsers;
    this.dialog.open(SubjectUserSecurityComponent, {
      data: {
        title: 'Select Users With Access',
        users: userList,  // pass empty array and let the dialog/component auto populate with available users
        selectedUsers,
        subject: entity,
      },
      autoFocus: false,
    }).afterClosed().subscribe((data) => {
      if (data) {
        // by default we don't pass any users, which will remove any security for the subject
        let users: User[] = data.selectedUsers;
        let msg = '';
        let redisplay = false;
        let reset = false;
        let cancelIfNo = false;
        let msgBoxType = MessageBoxType.YesNo;

        if (!users?.length) {
          // no-one selected
          msg = `No users have been selected.  Are you sure you want to remove security restrictions?`;
          redisplay = true;
          reset = true;
        }
        else {
          // is everyone selected?
          const available = (data.availableUsers as User[]).map((i) => i.id).join(',');
          const selected = (data.selectedUsers as User[]).map((i) => i.id).join(',');

          if (selected === available) {
            // everyone selected - check if should remove security restrictions
            const hasExisting = (initialUsers && (initialUsers.length > 0));
            const removeMsg = hasExisting ? 'remove restrictions' : 'cancel';
            cancelIfNo = !hasExisting;
            msgBoxType = (hasExisting) ? MessageBoxType.YesNoCancel : MessageBoxType.YesNo;
            msg = `Do you want to allow all active users access?  Click Yes to apply restrictions, No to ${removeMsg}.`;
          }
        }

        if (!msg) {
          // check if current user is included
          const currentUserId = this.userService.getCurrentUserId();
          if (!users.find((u) => u.id === currentUserId)) {
            // current user not included...
            msg = `You have not included yourself.  Do you want to continue?`;
            redisplay = true;
          }
        }

        if (msg) {
          const info = new MessageBoxParameters(msg, 'Security Restriction Changes', msgBoxType, true);
          this.dialog.open(MessageBoxComponent, { data: info, disableClose: false, autoFocus: false }).afterClosed().subscribe((result) => {
            if (result === 'cancel') {
              return;
            }
            if (result === 'no') {
              if (redisplay) {
                if (reset) {
                  this.apply(entity, initialUsers);
                }
                else {
                  this.apply(entity, users.map((u) => u.id));
                }
                return;
              }
              else {
                users = [];
              }
              if (cancelIfNo) {
                return;
              }
            }

            // process the changes...
            this.setSecurity(entity, users, data?.userSecurityDto || []);
          });
        }
        else {
          // process the changes...
          this.setSecurity(entity, users, data?.userSecurityDto || []);
        }
      }
    });
  }


  private setSecurity(entity: Subject, users: User[], userSecurityDto: UserSecurityDto[]) {
    if (entity) {
      this.subjectService.setUserSecurity(entity, users, userSecurityDto).subscribe((restricted) => {
        const currentUserId = this.userService.getCurrentUserId();
        const currentUserAllowedAccess = ((users.find((u) => u.id === currentUserId)) ? true : false) || (users.length === 0);
        if ((entity.restricted !== restricted) || (!currentUserAllowedAccess)) {
          // update the current entity with change to restricted flag
          entity.restricted = restricted;

          // notify listeners of change to entity security
          this.onSecurityChanged.next({ entity, currentUserAllowedAccess: (currentUserAllowedAccess) ? true : false });

          // push updated subject on to event service
          this.subjectService.getFullSubject(entity.uid).pipe(take(1)).subscribe((subject) => {
            this.eventService.updateSubject(subject);
          });
        }
        if (!currentUserAllowedAccess) {
          // current user not included, clear the UserSubjectHistory cache
          this.userSubjectHistoryService.forceRefresh();
        }

        // Delete users from HyperLawCase if one exists for Subject
        if (this.hyperLawService.isModuleActive()) {
          this.hyperLawService.getCase(entity.uid).subscribe((result: HyperLawCase) => {
            if (result) {
              const userEmails: string[] = users.filter((i) => i.email).map((i: User) => i?.email);
              this.hyperLawService.removeUsersFromCase(result.internalCaseId, userEmails);
            }
          })  
        }
      });
    }
  }
}
